import React, { useEffect } from "react";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Quote } from "lucide-react";
import {
  SiReact, SiNextdotjs, SiWordpress, SiJavascript, SiTypescript,
  SiNodedotjs, SiTailwindcss, SiFigma, SiMongodb,
  SiDocker, SiKubernetes, SiVercel, SiPython, SiFlutter, SiFirebase,
  SiGraphql, SiPhp
} from "react-icons/si";
import { FaAmazon, FaHtml5, FaCss3 } from "react-icons/fa";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import Process from "../components/Process";
import ParticleGame from "../components/ParticleGame";
import SectionStepper from "../components/SectionStepper";
import SEO from "../components/SEO";
import { technologies } from "../data/mock";

const iconMap = {
  "AWS": <FaAmazon />,
  "React": <SiReact />,
  "Next.js": <SiNextdotjs />,
  "WordPress": <SiWordpress />,
  "JavaScript": <SiJavascript />,
  "TypeScript": <SiTypescript />,
  "HTML5": <FaHtml5 />,
  "CSS3": <FaCss3 />,
  "Node.js": <SiNodedotjs />,
  "Tailwind CSS": <SiTailwindcss />,
  "Figma": <SiFigma />,
  "MongoDB": <SiMongodb />,
  "Docker": <SiDocker />,
  "Kubernetes": <SiKubernetes />,
  "Vercel": <SiVercel />,
  "Python": <SiPython />,
  "Flutter": <SiFlutter />,
  "Firebase": <SiFirebase />,
  "GraphQL": <SiGraphql />,
  "PHP": <SiPhp />
};

const stackSteps = [
  {
    step: "01",
    title: "Vetting",
    desc: "Every technology in our arsenal undergoes rigorous testing for performance, security, and developer ecosystem health.",
    image: "/images/vetting_thumb.png"
  },
  {
    step: "02",
    title: "Engineering",
    desc: "We don't just use tools; we master them. Building modular, type-safe, and highly maintainable codebases.",
    image: "/images/engineering_thumb.png"
  },
  {
    step: "03",
    title: "Hardening",
    desc: "Proactive security measures and performance audits to ensure your product remains fast and secure at scale.",
    image: "/images/hardening_thumb.png"
  },
  {
    step: "04",
    title: "Scaling",
    desc: "Designing for the future. Our stacks are built to handle rapid growth with zero technical debt.",
    image: "/images/scaling_thumb.png"
  }
];

const StackPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    window.lenis = lenis;
    return () => {
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  const stepperSections = [
    { id: "hero", label: "Arsenal" },
    { id: "manifesto", label: "Ethos" },
    { id: "arsenal", label: "Toolkit" },
    { id: "game", label: "Kinetic Lab" },
    { id: "expertise", label: "Design Ethos" },
    { id: "process", label: "Lifecycle" }
  ];

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO
        title="Digital Arsenal — Our Tech Stack"
        description="Discover the cutting-edge technologies Meraki Solutions uses to engineer world-class digital products."
      />
      {/* <CustomCursor /> */}
      <SectionStepper sections={stepperSections} />
      <div className="grain" />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section id="hero" className="relative pt-44 pb-32 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="w-full h-full object-cover opacity-45 grayscale brightness-90"
            >
              <source src="https://videos.pexels.com/video-files/4354448/4354448-uhd_2560_1440_25fps.mp4" type="video/mp4" />
            </video>
            {/* Animated Circuit lines effect */}
            <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(var(--gold) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          </div>

          <div className="container-xl relative z-20 px-4 md:px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1 }}
              className="max-w-4xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-xs mb-6 block">The Toolkit</span>
              <h1 className="font-display text-6xl md:text-9xl mb-12 leading-none">
                Digital <br /> <span className="italic gold-text">Arsenal</span>
              </h1>
              <p className="text-[var(--cream-muted)] text-xl font-body leading-relaxed max-w-2xl">
                We leverage the most advanced and reliable technologies to build scalable, high-performance digital solutions that drive business growth. Precision in code, excellence in execution.
              </p>
            </motion.div>
          </div>
        </section>

        <section id="manifesto" className="bg-[var(--ink)] relative">
          <StackManifesto />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[var(--rule)] to-transparent" />
        </section>

        {/* Stack Categories Section */}
        <section id="arsenal" className="pb-44">
          <div className="container-xl px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {technologies.map((tech, index) => (
                <motion.div
                  key={tech.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative p-10 rounded-3xl bg-[var(--ink-2)] border border-[var(--rule)] hover:border-[var(--gold)]/50 transition-all duration-500 overflow-hidden"
                >
                  <div className="absolute top-0 right-0 p-8 opacity-5 transform group-hover:scale-125 transition-transform duration-700 text-8xl">
                    {iconMap[tech.name]}
                  </div>

                  <div className="relative z-10">
                    <div className="text-4xl text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform duration-500 origin-left">
                      {iconMap[tech.name]}
                    </div>
                    <h3 className="font-display text-2xl mb-4 text-[var(--cream)]">{tech.name}</h3>
                    <p className="text-[var(--muted)] text-[12px] uppercase tracking-widest leading-loose">
                      {tech.category}
                    </p>

                    <div className="mt-8 pt-6 border-t border-[var(--rule)] flex items-center justify-between">
                      <span className="text-[9px] uppercase tracking-[0.3em] text-[var(--muted)]">Status</span>
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-[9px] uppercase tracking-[0.3em] text-[var(--gold)]">Active</span>
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <ParticleGame id="game" />

        {/* Expertise Section */}
        <section id="expertise" className="py-44 bg-[var(--ink-2)] border-y border-[var(--rule)]">
          <div className="container-xl px-4 md:px-6 grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-xs mb-6 block">Core Principles</span>
              <h2 className="font-display text-5xl md:text-7xl mb-8">Performance <br /><span className="italic gold-text">by Design</span></h2>
              <div className="space-y-8">
                {[
                  { title: "Scalability", desc: "Architecture built to grow from MVP to global scale without technical debt." },
                  { title: "Security", desc: "Hardened systems with proactive monitoring and best-in-class encryption." },
                  { title: "Optimization", desc: "Pixel-perfect performance with sub-second load times and efficient rendering." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="font-impact text-[var(--gold)] opacity-50">0{i + 1}</div>
                    <div>
                      <h4 className="font-display text-xl text-[var(--cream)] mb-2">{item.title}</h4>
                      <p className="text-[var(--cream-muted)] text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative aspect-square rounded-[40px] overflow-hidden border border-[var(--rule)]">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-cover grayscale opacity-40"
              >
                <source src="https://videos.pexels.com/video-files/7947485/7947485-hd_1920_1080_30fps.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-tr from-[var(--gold)]/20 to-transparent" />
            </div>
          </div>
        </section>

        <Process 
          id="process"
          steps={stackSteps} 
          title="Engineering Excellence" 
          kicker={{ index: "03", label: "The Tech Lifecycle" }} 
        />
      </main>

      <Footer />
    </div>
  );
};

const StackManifesto = () => {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"]
  });

  const text = "Engineering excellence is non-negotiable. We build digital foundations that are modular, type-safe, and future-proof. Our goal isn't just to ship code, but to engineer legacies that perform at scale and remain secure under pressure.";
  const words = text.split(" ");

  return (
    <div ref={container} className="relative h-[600vh] bg-[var(--ink)]">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="container-xl px-4 md:px-6 w-full">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12 md:mb-20">
              <Kicker index="02" label="Our Philosophy" />
              <h2 className="mt-6 font-display text-3xl md:text-6xl leading-tight">
                Engineering <span className="italic gold-text">Absolute Precision.</span>
              </h2>
            </div>
            
            <div className="relative">
              <div className="absolute -top-12 -left-12 opacity-5 hidden lg:block">
                <Quote size={180} className="text-[var(--gold)]" />
              </div>
              
              <div className="flex flex-wrap text-[clamp(20px,4.5vw,56px)] font-display italic leading-[1.25] text-[var(--cream)]">
                {words.map((word, i) => {
                  const start = (i / words.length) * 0.75;
                  const end = start + (1 / words.length) * 0.75;
                  return (
                    <Word key={i} progress={scrollYProgress} range={[start, end]}>
                      {word}
                    </Word>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 w-full h-1 bg-white/5">
          <motion.div 
            className="h-full bg-[var(--gold)]"
            style={{ scaleX: scrollYProgress, transformOrigin: "0%" }}
          />
        </div>
      </div>
    </div>
  );
};

const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.12, 1]);
  return (
    <span className="relative inline-block mr-[0.35em] mb-[0.2em]">
      <span className="absolute top-0 left-0 opacity-[0.04] select-none pointer-events-none whitespace-nowrap">
        {children}
      </span>
      <motion.span style={{ opacity }} className="relative z-10 whitespace-nowrap">
        {children}
      </motion.span>
    </span>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default StackPage;
